import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Box, Drawer, IconButton, Toolbar, Typography } from "@mui/material";
import { useEffect, useRef, type ReactNode } from "react";

export const SIDEBAR_DRAWER_WIDTH = 240;
export const SIDEBAR_RAIL_WIDTH = 72;
/** Shared open/close timing so root, paper, and Slide stay in sync. */
export const SIDEBAR_TRANSITION_MS = 280;
/** Decelerate into rest — no overshoot, no ease-in-out “rubber” feel. */
const sidebarEase = "cubic-bezier(0.25, 0.1, 0.25, 1)";
const sidebarWidthTransition = `width ${SIDEBAR_TRANSITION_MS}ms ${sidebarEase}`;

/**
 * Layout chrome stays fully expanded; the shell clips to rail width on desktop.
 * Mobile temporary drawers always show full labels while sliding closed.
 */
export function useSidebarNavOpen(_open: boolean, _isMobile: boolean) {
  return true;
}

/** Close the temporary drawer on route change, but not on the initial mount. */
export function useCloseDrawerOnRouteChange(
  isMobile: boolean,
  pathname: string,
  handleClose?: () => void
) {
  const prevPathRef = useRef(pathname);

  useEffect(() => {
    if (!isMobile || !handleClose) return;
    if (prevPathRef.current === pathname) return;
    prevPathRef.current = pathname;
    handleClose();
  }, [pathname, isMobile, handleClose]);
}

const goldRule =
  "linear-gradient(90deg, transparent 5%, var(--neutral-200) 20%, var(--neutral-400) 50%, var(--neutral-200) 80%, transparent 95%)";

export const sidebarTheme = {
  bg: "var(--page-bg)",
  logoCardBg: "var(--neutral-50)",
  border: "var(--neutral-100)",
  borderSubtle: "var(--neutral-100)",
  topRule: goldRule,
  divider: goldRule,
  shadow: "none",
  scrollbarThumb: "var(--neutral-300)",
  scrollbarThumbHover: "var(--neutral-500)",
  idleText: "var(--text-muted)",
  activeText: "var(--text)",
  activeBg: "light-dark(#f2f2f2, #1a1a1a)",
  activeBgHover: "light-dark(#eaeaea, #222222)",
  hoverBg: "light-dark(#f7f7f7, #141414)",
  activeBorder: "var(--neutral-200)",
  coffeeBannerBg: "light-dark(#f4f4f4, #1a1a1a)",
  coffeeBannerBorder: "var(--neutral-200)",
  coffeeBannerShadow: "none",
} as const;

const goldFoil = {
  color: "var(--text)",
  WebkitTextFillColor: "unset",
  background: "none",
};

type TaasSidebarLogoProps = {
  compact?: boolean;
};

export function TaasSidebarLogo({ compact = false }: TaasSidebarLogoProps) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: compact ? 0 : 1.25 }}>
      <Box
        sx={{
          position: "relative",
          width: compact ? 28 : 32,
          height: compact ? 28 : 32,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: compact ? 20 : 22,
            height: compact ? 20 : 22,
            border: "1px solid var(--primary-600)",
            borderRadius: "2px",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: compact ? 20 : 22,
            height: compact ? 20 : 22,
            border: "1px solid var(--primary-600)",
            borderRadius: "2px",
            bgcolor: sidebarTheme.logoCardBg,
          }}
        />
      </Box>
      {!compact && (
        <Typography
          sx={{
            fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
            fontSize: "1.2rem",
            fontWeight: 600,
            letterSpacing: "-0.03em",
            lineHeight: 1,
            whiteSpace: "nowrap",
            color: "var(--text)",
          }}
        >
          thehope.one
        </Typography>
      )}
    </Box>
  );
}

/** Wordmark sits past the rail so it clips away when collapsed; mark stays centered. */
export function TaasSidebarLogoRail() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%", minHeight: 72 }}>
      <Box
        sx={{
          width: SIDEBAR_RAIL_WIDTH,
          minWidth: SIDEBAR_RAIL_WIDTH,
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <TaasSidebarLogo compact />
      </Box>
      <Typography
        sx={{
          fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
          fontSize: "1.2rem",
          fontWeight: 600,
          letterSpacing: "-0.03em",
          lineHeight: 1,
          whiteSpace: "nowrap",
          pr: 2,
          color: "var(--text)",
        }}
      >
        thehope.one
      </Typography>
    </Box>
  );
}

/** Gold rule when open; hidden when the rail is collapsed. */
export function GoldSidebarDivider({
  width = "85%",
  open = true,
}: {
  width?: string;
  open?: boolean;
}) {
  if (!open) return null;

  return (
    <Box
      role="separator"
      sx={{
        mx: "auto",
        width,
        maxWidth: `calc(100% - ${SIDEBAR_RAIL_WIDTH}px - 16px)`,
        my: 2,
        height: "1px",
        minHeight: "1px",
        maxHeight: "1px",
        flexShrink: 0,
        flexGrow: 0,
        overflow: "hidden",
        background: sidebarTheme.divider,
      }}
    />
  );
}

/** Section label + trailing rule; collapsed rail shows only a short centered line. */
export function SidebarSectionHeader({
  label,
  open,
  muted = "neutral",
}: {
  label: string;
  open: boolean;
  /** User Figma uses light gray; talent uses soft charcoal. */
  muted?: "neutral" | "charcoal";
}) {
  const color =
    muted === "charcoal" ? "var(--sidebar-section-charcoal)" : "var(--sidebar-section-neutral)";
  const opacity = muted === "charcoal" ? 0.52 : 1;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: open ? "space-between" : "center",
        gap: open ? 1 : 0,
        px: open ? 1.25 : 0,
        mb: 1.75,
        minHeight: 20,
        width: open ? "100%" : SIDEBAR_RAIL_WIDTH,
        minWidth: open ? undefined : SIDEBAR_RAIL_WIDTH,
        overflow: "hidden",
      }}
      aria-hidden={!open}
    >
      {open ? (
        <Typography
          component="p"
          sx={{
            m: 0,
            flexShrink: 0,
            fontFamily: "var(--primary-font-family)",
            fontSize: 15,
            fontWeight: 400,
            lineHeight: 1.2,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color,
            opacity,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
      ) : null}
      <Box
        role="presentation"
        sx={{
          flex: open ? 1 : "none",
          width: open ? "auto" : 28,
          minWidth: open ? 24 : 28,
          height: "1px",
          bgcolor: color,
          opacity: opacity * 0.85,
        }}
      />
    </Box>
  );
}

type SidebarShellProps = {
  open: boolean;
  isMobile: boolean;
  onClose?: () => void;
  /** Desktop: expand/collapse rail. Mobile: same as close when open. */
  onToggle?: () => void;
  body: ReactNode;
  footer?: ReactNode;
};

export function SidebarShell({
  open,
  isMobile,
  onClose,
  onToggle,
  body,
  footer,
}: SidebarShellProps) {
  // Mobile temporary: always full width + Slide off-screen (never shrink to rail).
  // Desktop permanent: animate outer width only; inner chrome stays 240px and clips.
  // That avoids reflowing icons/labels mid-close, which reads as a bounce.
  const paperWidth = isMobile
    ? SIDEBAR_DRAWER_WIDTH
    : open
      ? SIDEBAR_DRAWER_WIDTH
      : SIDEBAR_RAIL_WIDTH;

  // Pin to the paper (visible width), not the 240px inner box — so << / >>
  // stays in the top-right when the rail is collapsed.
  const showToggle = Boolean(onToggle) && (!isMobile || open);

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      onClose={onClose}
      transitionDuration={{
        enter: SIDEBAR_TRANSITION_MS,
        exit: SIDEBAR_TRANSITION_MS,
      }}
      SlideProps={{
        easing: {
          enter: sidebarEase,
          exit: sidebarEase,
        },
      }}
      ModalProps={{
        keepMounted: true,
      }}
      slotProps={
        isMobile
          ? {
              backdrop: {
                transitionDuration: SIDEBAR_TRANSITION_MS,
              },
            }
          : undefined
      }
      sx={{
        width: isMobile ? 0 : paperWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        // Let the edge toggle sit on the border without being clipped.
        overflow: "visible",
        // Only permanent drawers animate width; temporary uses Slide transform only.
        ...(!isMobile ? { transition: sidebarWidthTransition } : null),
        "& .MuiDrawer-paper": {
          width: paperWidth,
          ...(!isMobile ? { transition: sidebarWidthTransition } : null),
          position: "relative",
          bgcolor: sidebarTheme.bg,
          backgroundImage: "none",
          borderRight: `1px solid ${sidebarTheme.border}`,
          // Inner content still clips; paper stays open so << / >> can sit on the edge.
          overflow: "visible",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          pt: 13,
          fontFamily: "var(--primary-font-family)",
          boxShadow: isMobile ? "var(--shadow-md)" : sidebarTheme.shadow,
          // Avoid layout-affecting shadow animation during Slide close.
          willChange: isMobile ? "transform" : "width",
          scrollbarWidth: "thin",
          scrollbarColor: `${sidebarTheme.scrollbarThumb} transparent`,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: sidebarTheme.scrollbarThumb,
            borderRadius: "10px",
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: sidebarTheme.scrollbarThumbHover,
          },
        },
      }}
    >
      {showToggle ? (
        <IconButton
          onClick={onToggle}
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
          size="small"
          sx={{
            position: "absolute",
            top: 80,
            right: 0,
            transform: "translateX(50%)",
            zIndex: 2,
            width: 28,
            height: 28,
            p: 0,
            color: "var(--text)",
            bgcolor: "var(--page-bg)",
            border: "1px solid var(--neutral-200)",
            borderRadius: "999px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
            "&:hover": {
              bgcolor: "var(--neutral-50)",
              borderColor: "var(--neutral-400)",
            },
          }}
        >
          {open ? (
            <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
          ) : (
            <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      ) : null}

      {/* Fixed inner width: outer paper clips during rail collapse — no layout bounce. */}
      <Box
        sx={{
          width: SIDEBAR_DRAWER_WIDTH,
          minWidth: SIDEBAR_DRAWER_WIDTH,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {body}
        {footer ? (
          <Box sx={{ flexShrink: 0, width: "100%", overflow: "hidden", mt: "auto" }}>{footer}</Box>
        ) : null}
      </Box>
    </Drawer>
  );
}

export const guestAuthButtonSx = {
  outline: {
    fontWeight: 600,
    color: "var(--primary-600)",
    textTransform: "none" as const,
    fontFamily: "var(--primary-font-family)",
    border: "1px solid var(--primary-400)",
    borderRadius: "8px",
    py: 1,
    "&:hover": {
      borderColor: "var(--primary-600)",
      bgcolor: "var(--primary-100)",
    },
  },
  primary: {
    background: "var(--button-gradient)",
    color: "var(--primary-1000)",
    fontWeight: 700,
    textTransform: "none" as const,
    fontFamily: "var(--primary-font-family)",
    borderRadius: "8px",
    py: 1,
    boxShadow: "var(--shadow-sm)",
    "&:hover": {
      background: "var(--button-gradient-hover)",
      boxShadow: "var(--shadow-md)",
    },
  },
};
